import type {
  PresetColorType,
  PresetStatusColorType,
} from 'antd/es/_util/colors';

import { BOOKING_REFUND_SOURCE } from 'app/typings/core/common/enum/bookingRefundSource';
import { ISSUING_TRANSACTION_STATUS } from 'app/typings/core/common/enum/issuingTransactionStatus';
import { ISSUING_TRANSACTION_TYPE } from 'app/typings/core/common/enum/issuingTransactionType';
import type { VccStatusDisplayInfo } from 'app/typings/core/common/enum/vccStatus';
import { VCC_STATUS } from 'app/typings/core/common/enum/vccStatus';
import type {
  BookingDetails,
  BookingIssuingTransactionResponse,
  BookingRefundResponse,
} from 'app/typings/core/extranet/bookings/booking';
import { DEACTIVATE_AFTER_X_MONTHS } from 'app/typings/vcc';
import { dateAndHour } from 'app/utils/dates';
import {
  addMonths,
  isAfter,
  isBefore,
  isFuture,
  isToday,
} from 'app/utils/dayjs';

type VccStatusConfig = {
  color: PresetColorType | PresetStatusColorType;
  text: string;
};

type VccHistoryType = { date: string; text: string };

export const MODERN_VCC_STATUS_CONFIG: Record<VCC_STATUS, VccStatusConfig> = {
  [VCC_STATUS.INACTIVE]: {
    color: 'default',
    text: 'Inactive',
  },
  [VCC_STATUS.CANCELED]: {
    color: 'red',
    text: 'Canceled',
  },
  [VCC_STATUS.TO_CHARGE]: {
    color: 'blue',
    text: 'To Charge',
  },
  [VCC_STATUS.CHARGED]: {
    color: 'green',
    text: 'Charged',
  },
  [VCC_STATUS.TO_REFUND]: {
    color: 'gold',
    text: 'To Refund',
  },
  [VCC_STATUS.DEACTIVATED]: {
    color: 'default',
    text: 'Deactivated',
  },
};

/**
 * Determines the VCC status and display information for a booking.
 * Prioritizes the `vccStatus` field from BookingDetails if available,
 * otherwise calculates the status based on booking properties.
 *
 * @param booking - The booking details
 * @param currencyFormatter - Function to format currency values
 * @returns VCC status display information including status, text, color, and history
 */
export const getVCCStatus = (
  booking: BookingDetails,
  currencyFormatter: (value: number) => string
): VccStatusDisplayInfo => {
  const formattedCheckIn = booking.checkIn
    ? new Date(booking.checkIn)
    : new Date();

  const history = getVCCHistory(booking, currencyFormatter);

  if (booking.vccStatus) {
    const { text, color } = MODERN_VCC_STATUS_CONFIG[booking.vccStatus];

    return {
      status: booking.vccStatus,
      text,
      color,
      history,
    };
  }

  const isFutureCheckIn =
    isFuture(formattedCheckIn) && !isToday(formattedCheckIn);
  const isFullyCharged = booking.issuingBalance === 0;
  const isDeactivated =
    !isFullyCharged &&
    isAfter(new Date(), addMonths(formattedCheckIn, DEACTIVATE_AFTER_X_MONTHS));
  const isCancelled = booking.issuingBalance > 0 && booking.refunds.length > 0;

  let status: VCC_STATUS;

  if (isCancelled) {
    status = VCC_STATUS.CANCELED;
  } else if (isFutureCheckIn) {
    status = VCC_STATUS.INACTIVE;
  } else if (isDeactivated) {
    status = VCC_STATUS.DEACTIVATED;
  } else if (booking.issuingBalance > 0) {
    status = VCC_STATUS.TO_CHARGE;
  } else if (booking.issuingBalance < 0) {
    status = VCC_STATUS.TO_REFUND;
  } else {
    status = VCC_STATUS.INACTIVE;
  }

  const { text, color } = MODERN_VCC_STATUS_CONFIG[status];

  return {
    status,
    text,
    color,
    history,
  };
};

/**
 * Builds a history entry for a charge transaction on the VCC.
 */
const addChargedAtToHistory = (
  transaction: BookingIssuingTransactionResponse,
  currencyFormatter: (value: number) => string
) => {
  const date =
    transaction.authorizedAt ||
    transaction.chargedAt ||
    new Date().toISOString();

  return {
    date: date,
    text: `${currencyFormatter(transaction.amount)} charged • ${dateAndHour(
      date
    )}`,
  };
};

/**
 * Builds a history entry for a refund transaction back to the VCC.
 */
const addRefundedToHistory = (
  transaction: BookingIssuingTransactionResponse,
  currencyFormatter: (value: number) => string
) => {
  return {
    date: transaction.chargedAt ?? new Date().toISOString(),
    text: `${currencyFormatter(
      transaction.amount
    )} refunded to VCC • ${dateAndHour(
      transaction.chargedAt ?? new Date().toISOString()
    )}`,
  };
};

/**
 * Builds a history entry for a refund request initiated by the hotel.
 * Sums all refund amounts (credit card, gift card, and credit).
 */
const addRefundRequestedToHistory = (
  refund: BookingRefundResponse,
  currencyFormatter: (value: number) => string
) => {
  const totalRefundAmount =
    (refund.creditCardAmount ?? 0) +
    (refund.giftCardAmount ?? 0) +
    (refund.creditAmount ?? 0);

  return {
    date: refund.createdAt,
    text: `Refund requested, amount due ${currencyFormatter(
      totalRefundAmount
    )} • ${dateAndHour(refund.createdAt)}`,
  };
};

/**
 * Builds a chronological history of VCC transactions and refund requests for a booking.
 * Includes:
 * - Charge transactions (PENDING or VALIDATED CAPTURE)
 * - Refund transactions (PENDING or VALIDATED REFUND)
 * - Hotel refund requests
 *
 * @param booking - The booking details
 * @param currencyFormatter - Function to format currency values
 * @returns Array of formatted history entries sorted chronologically (oldest first)
 */
const getVCCHistory = (
  booking: BookingDetails,
  currencyFormatter: (value: number) => string
): string[] => {
  const history: VccHistoryType[] = [];

  const chargedTransactions = booking.issuingTransactions.filter(
    (transaction) =>
      transaction.status === ISSUING_TRANSACTION_STATUS.PENDING ||
      (transaction.status === ISSUING_TRANSACTION_STATUS.VALIDATED &&
        transaction.type === ISSUING_TRANSACTION_TYPE.CAPTURE &&
        (transaction.authorizedAt || transaction.chargedAt))
  );

  chargedTransactions?.forEach((transaction) => {
    history.push(addChargedAtToHistory(transaction, currencyFormatter));
  });

  const refundedTransactions = booking.issuingTransactions.filter(
    (transaction) =>
      (transaction.status === ISSUING_TRANSACTION_STATUS.PENDING ||
        transaction.status === ISSUING_TRANSACTION_STATUS.VALIDATED) &&
      transaction.type === ISSUING_TRANSACTION_TYPE.REFUND &&
      transaction.chargedAt
  );

  refundedTransactions?.forEach((transaction) => {
    history.push(addRefundedToHistory(transaction, currencyFormatter));
  });

  const hotelRefunds = booking.refunds.filter(
    (refund) => refund.source === BOOKING_REFUND_SOURCE.HOTEL
  );

  hotelRefunds.forEach((refund) => {
    history.push(addRefundRequestedToHistory(refund, currencyFormatter));
  });

  return history
    .sort((a, b) => (isBefore(new Date(a.date), new Date(b.date)) ? -1 : 1))
    .map((entry) => entry.text);
};
